import axios from 'axios'
import { addMinutes } from 'date-fns'

import { removeLeaflet } from '@/api/leaflets'
import { fetchLeaflets, updateLeaflet } from '@/api/repositories/leafletsRepository'
import { createShop, fetchShop, fetchShops, fetchShopsLeaflets, updateShop } from '@/api/repositories/shopsRepository'
import { addShopLogo, getLeafletsSummary, removeShop } from '@/api/shops'
import { statusList } from '@/views/dashboard/dashboard.constants'

const getLeafletsToActivationFromStores = (stores = []) =>
  stores.reduce((acc, store) => {
    const filteredLeaflets =
      store.leaflets &&
      store.leaflets.filter(
        leaflet =>
          leaflet.status === statusList.READY_FOR_ACTIVATION.value &&
          new Date(leaflet.publishDate) < addMinutes(new Date(), 5)
      )

    if (filteredLeaflets.length) {
      return [
        ...acc,
        {
          name: store.name,
          id: store.id,
          leafletsToActivation: filteredLeaflets.map(leaflet => leaflet.id),
          numberOfLeafletsToActivation: filteredLeaflets.length
        }
      ]
    }
    return acc
  }, [])

const getLeafletsToDisactivationFromStores = stores =>
  stores.reduce((acc, store) => {
    const filteredLeaflets =
      store.leaflets && store.leaflets.filter(leaflet => leaflet.status === statusList.READY_FOR_DISACTIVATION.value)

    if (filteredLeaflets.length) {
      return [
        ...acc,
        {
          name: store.name,
          id: store.id,
          leafletsToDisactivation: filteredLeaflets.map(leaflet => leaflet.id),
          numberOfLeafletsToDisactivation: filteredLeaflets.length
        }
      ]
    }
    return acc
  }, [])

const insertShopSorted = (shopList, shop) => {
  const insertIndex = shopList.findIndex(s => s.name.localeCompare(shop.name) > 0)
  if (insertIndex === -1) {
    shopList.push(shop)
  } else {
    shopList.splice(insertIndex, 0, shop)
  }
}

const shops = {
  state: {
    shopList: [],
    dataLoading: false,
    leafletLoader: false,
    hasPreviousState: false,
    popstate: false,
    openedStores: [],
    leafletsToActivation: null,
    leafletsToDisactivation: null
  },
  mutations: {
    ADD_OPENED_STORE: (state, storeId) => {
      state.openedStores = [...state.openedStores, storeId]
    },
    REMOVE_OPENED_STORE: (state, storeId) => {
      state.openedStores = state.openedStores.filter(openedStore => openedStore !== storeId)
    },
    SET_ALL_STORES_OPENED: state => {
      state.openedStores = state.shopList.map(shop => shop.id) || []
    },
    REMOVE_ALL_OPENED_STORES: state => {
      state.openedStores = []
    },

    // STORES MUTATIONS
    SET_SHOPS: (state, shopsData) => {
      state.shopList = shopsData
    },
    ADD_NEW_SHOP: (state, shop) => {
      insertShopSorted(state.shopList, shop)
    },
    REMOVE_SHOP: (state, shopId) => {
      state.shopList = state.shopList.filter(shop => shop.id !== shopId)
    },
    UPDATE_SHOP: (state, shopToUpdate) => {
      const { leaflets } = state.shopList.find(shop => shop.id === shopToUpdate.id)

      state.shopList = state.shopList.filter(shop => shop.id !== shopToUpdate.id)
      insertShopSorted(state.shopList, { ...shopToUpdate, leaflets })
    },
    SWITCH_STORE_INACTIVE_FILTER: (state, storeId) => {
      state.shopList = state.shopList.map(shop => {
        if (shop.id === storeId) {
          return { ...shop, filterByInactive: !shop.filterByInactive }
        }
        return shop
      }, [])
    },

    // LEAFLETS MUTATIONS
    ADD_LEAFLETS_TO_STORE: (state, data) => {
      state.shopList = state.shopList.map(shop => {
        if (shop.id === data.storeId) {
          const updatedLeaflets = data.leaflets.map(newLeaflet => {
            const oldLeaflet = shop.leaflets.find(oldLeafletData => oldLeafletData.id === newLeaflet.id)

            if (oldLeaflet) {
              return { ...oldLeaflet, ...newLeaflet }
            }

            return newLeaflet
          })

          return { ...shop, leaflets: updatedLeaflets, filterByInactive: false }
        }

        return shop
      })
    },
    REMOVE_LEAFLET: (state, { storeId, leafletId }) => {
      state.shopList = state.shopList.map(shop => {
        if (shop.id === storeId) {
          return { ...shop, leaflets: shop.leaflets.filter(leaflet => leaflet.id !== leafletId) }
        }
        return shop
      })
    },
    SET_LEAFLET_STATUS: (state, { storeId, leafletId, status }) => {
      state.shopList = state.shopList.map(shop => {
        if (shop.id === storeId) {
          return {
            ...shop,
            leaflets: shop.leaflets.map(leaflet => {
              if (leaflet.id === leafletId) {
                return { ...leaflet, status }
              }
              return leaflet
            })
          }
        }
        return shop
      })
    },
    SET_DATA_LOADER: state => {
      state.dataLoading = true
    },
    DISABLE_DATA_LOADER: state => {
      state.dataLoading = false
    },
    SET_LEAFLET_LOADER: state => {
      state.leafletLoader = true
    },
    DISABLE_LEAFLET_LOADER: state => {
      state.leafletLoader = false
    },
    HAS_PREVIOUS_STATE: state => {
      state.hasPreviousState = true
    },
    POPSTATE_INVOKED: state => {
      state.popstate = true
    },
    POPSTATE_RESTORED: state => {
      state.popstate = false
    },
    SET_LEAFLETS_SUMMARY: (state, { storeId, leafletsSummary }) => {
      state.shopList = state.shopList.map(shop => {
        if (shop.id === storeId) {
          return { ...shop, ...leafletsSummary }
        }

        return shop
      })
    },
    SET_LEAFLETS_TO_ACTIVATION: (state, leafletsToActivation) => {
      state.leafletsToActivation = leafletsToActivation
    },
    SET_LEAFLETS_TO_DISACTIVATION: (state, leafletsToDisactivation) => {
      state.leafletsToDisactivation = leafletsToDisactivation
    },
    REMOVE_LEAFLET_TO_ACTIVATION: (state, { storeId, leafletId }) => {
      state.leafletsToActivation = state.leafletsToActivation.reduce((acc, shop) => {
        if (shop.id === storeId) {
          const newLeafletsToActivation = shop.leafletsToActivation.filter(
            currentLeafletId => currentLeafletId !== leafletId
          )

          if (!newLeafletsToActivation.length) {
            return acc
          }

          return [
            ...acc,
            {
              ...shop,
              leafletsToActivation: newLeafletsToActivation,
              numberOfLeafletsToActivation: newLeafletsToActivation.length
            }
          ]
        }

        return [...acc, shop]
      }, [])
    },
    REMOVE_LEAFLET_TO_DISACTIVATION: (state, { storeId, leafletId }) => {
      state.leafletsToDisactivation = state.leafletsToDisactivation.reduce((acc, shop) => {
        if (shop.id === storeId) {
          const newLeafletsToDisactivation = shop.leafletsToDisactivation.filter(
            currentLeafletId => currentLeafletId !== leafletId
          )

          if (!newLeafletsToDisactivation.length) {
            return acc
          }

          return [
            ...acc,
            {
              ...shop,
              leafletsToDisactivation: newLeafletsToDisactivation,
              numberOfLeafletsToDisactivation: newLeafletsToDisactivation.length
            }
          ]
        }

        return [...acc, shop]
      }, [])
    },
    REMOVE_ARTICLES_DATA: (state, { storeIndex }) => {
      const shopListCopy = state.shopList
      const store = shopListCopy[storeIndex]
      shopListCopy[storeIndex] = {
        ...store
      }
      state.shopList = [...shopListCopy]
    }
  },
  actions: {
    setAllStoresOpened({ commit }) {
      localStorage.setItem('storesOpen', 'true')
      commit('SET_ALL_STORES_OPENED')
    },
    setAllStoresClosed({ commit }) {
      localStorage.setItem('storesOpen', 'false')
      commit('REMOVE_ALL_OPENED_STORES')
    },
    getLeafletsToActivation({ commit, state }) {
      const leafletsToActivation = getLeafletsToActivationFromStores(state.shopList)

      commit('SET_LEAFLETS_TO_ACTIVATION', leafletsToActivation)
    },
    getLeafletsToDisactivation({ commit, state }) {
      const leafletsToDisactivation = getLeafletsToDisactivationFromStores(state.shopList)

      commit('SET_LEAFLETS_TO_DISACTIVATION', leafletsToDisactivation)
    },
    async addShops({ commit }, shopsData) {
      await commit('SET_SHOPS', shopsData)
    },
    async getShops({ dispatch, commit, state }) {
      commit('SET_DATA_LOADER')

      try {
        const fetchedShops = await fetchShops()

        await dispatch(
          'addShops',
          fetchedShops.map(store => {
            const leafletsForAlreadyLoadedShop =
              state.shopList.length && state.shopList.find(shop => shop.id === store.id)

            return {
              ...store,
              leaflets: leafletsForAlreadyLoadedShop ? leafletsForAlreadyLoadedShop.leaflets : [],
              filterByInactive: false
            }
          })
        )

        if (localStorage.getItem('storesOpen') === 'true' && !state.hasPreviousState) {
          commit('SET_ALL_STORES_OPENED')
        } else if (localStorage.getItem('storesOpen') === 'false' && !state.hasPreviousState) {
          commit('REMOVE_ALL_OPENED_STORES')
        }
        commit('DISABLE_DATA_LOADER')
      } catch (e) {
        commit('DISABLE_DATA_LOADER')
      }
    },

    async getSingleShop({ commit, state }, shopId) {
      const data = await fetchShop(shopId)
      const shopExists = state.shopList.find(shop => shop.id === data.id)

      if (shopExists) {
        commit('UPDATE_SHOP', data)
      } else {
        commit('ADD_NEW_SHOP', data)
      }

      return data
    },

    async uploadShopLogo(_, { file, id, theme }) {
      const formData = new FormData()
      formData.append('file', file)

      await addShopLogo(formData, id, theme)
    },

    async uploadShopLogos({ dispatch }, { id, lightLogoFile, darkLogoFile }) {
      if (lightLogoFile) {
        await dispatch('uploadShopLogo', { file: lightLogoFile, id, theme: 'LIGHT' })
      }
      if (darkLogoFile) {
        await dispatch('uploadShopLogo', { file: darkLogoFile, id, theme: 'DARK' })
      }
    },

    async addNewShop({ dispatch }, { storeData, lightLogoFile, darkLogoFile }) {
      const data = await createShop(storeData)

      await dispatch('uploadShopLogos', { id: data.id, lightLogoFile, darkLogoFile })
      await dispatch('getSingleShop', data.id)
    },

    async updateExistingShop({ dispatch }, { storeData, lightLogoFile, darkLogoFile }) {
      await updateShop(storeData)

      await dispatch('uploadShopLogos', { id: storeData.id, lightLogoFile, darkLogoFile })
      await dispatch('getSingleShop', storeData.id)
    },

    async deleteShop({ commit }, { storeId }) {
      const response = await removeShop(storeId)

      commit('REMOVE_SHOP', storeId)
      return response.ok
    },

    async getLeafletsForAllStores({ commit, dispatch, state }) {
      commit('SET_LEAFLET_LOADER')
      const cancelToken = axios.CancelToken.source()
      const leaflets = await fetchLeaflets({ predefinedFilter: 'LEAFLET_DASHBOARD' }, cancelToken)

      state.shopList.map(store => {
        commit('ADD_LEAFLETS_TO_STORE', {
          storeId: store.id,
          leaflets: leaflets.filter(leaflet => leaflet.storeId === store.id)
        })
      })

      dispatch('getLeafletsToActivation')
      dispatch('getLeafletsToDisactivation')
      commit('DISABLE_LEAFLET_LOADER')
    },
    updateLeafletsToActivationForSingleStore({ state, commit }, { storeId, leaflets, storeName }) {
      const [leafletsToActivationInSingleStore] = getLeafletsToActivationFromStores([{ id: storeId, leaflets }])

      if (!leafletsToActivationInSingleStore) return

      const storeIndexInLeafletsToActivation = state.leafletsToActivation.findIndex(
        leafletsPerStore => leafletsPerStore.id === leafletsToActivationInSingleStore.id
      )

      if (storeIndexInLeafletsToActivation !== -1) {
        const stateCopy = [...state.leafletsToActivation]

        stateCopy[storeIndexInLeafletsToActivation] = {
          ...stateCopy[storeIndexInLeafletsToActivation],
          leafletsToActivation: leafletsToActivationInSingleStore.leafletsToActivation,
          numberOfLeafletsToActivation: leafletsToActivationInSingleStore.numberOfLeafletsToActivation
        }

        commit('SET_LEAFLETS_TO_ACTIVATION', stateCopy)
      } else {
        commit('SET_LEAFLETS_TO_ACTIVATION', [
          ...state.leafletsToActivation,
          { ...leafletsToActivationInSingleStore, name: storeName }
        ])
      }
    },
    updateLeafletsToDisactivationForSingleStore({ state, commit }, { storeId, leaflets, storeName }) {
      const [leafletsToDisactivationInSingleStore] = getLeafletsToDisactivationFromStores([{ id: storeId, leaflets }])

      if (!leafletsToDisactivationInSingleStore) return

      const storeIndexInLeafletsToDisactivation = state.leafletsToDisactivation.findIndex(
        leafletsPerStore => leafletsPerStore.id === leafletsToDisactivationInSingleStore.id
      )

      if (storeIndexInLeafletsToDisactivation !== -1) {
        const stateCopy = [...state.leafletsToDisactivation]

        stateCopy[storeIndexInLeafletsToDisactivation] = {
          ...stateCopy[storeIndexInLeafletsToDisactivation],
          leafletsToDisactivation: leafletsToDisactivationInSingleStore.leafletsToDisactivation,
          numberOfLeafletsToDisactivation: leafletsToDisactivationInSingleStore.numberOfLeafletsToDisactivation
        }
        commit('SET_LEAFLETS_TO_DISACTIVATION', stateCopy)
      } else {
        commit('SET_LEAFLETS_TO_DISACTIVATION', [
          ...state.leafletsToDisactivation,
          { ...leafletsToDisactivationInSingleStore, name: storeName }
        ])
      }
    },

    async getLeafletsForSingleStore({ commit, dispatch }, { storeId, storeName }) {
      const data = await fetchShopsLeaflets(storeId)

      commit('ADD_LEAFLETS_TO_STORE', { storeId, leaflets: data })

      dispatch('updateLeafletsToActivationForSingleStore', { storeId, leaflets: data, storeName })
      dispatch('updateLeafletsToDisactivationForSingleStore', {
        storeId,
        leaflets: data,
        storeName
      })
    },

    async removeLeafletFromStore({ commit }, data) {
      const response = await removeLeaflet(data.leafletId, data.storeId)

      if (response.status === 204) {
        commit('REMOVE_LEAFLET', data)
        commit('REMOVE_LEAFLET_TO_ACTIVATION', data)
        commit('REMOVE_LEAFLET_TO_DISACTIVATION', data)
      }
    },

    async activateLeaflet({ commit, state }, { storeId, leafletNaturalId }) {
      const shopData = state.shopList.find(shop => shop.id === storeId)
      const leaflet = shopData.leaflets.find(leaflet => leaflet.naturalId === leafletNaturalId)
      const leafletCut = {
        ...leaflet,
        untilEndOfSupply: leaflet.untilEndOfSupply || false,
        status: statusList.READY_FOR_ACTIVATION.value,
        notShowAdInserts: leaflet.notShowAdInserts || false,
        notAllowShoppingList: leaflet.notAllowShoppingList || false
      }
      if (leafletCut.untilEndOfSupply) {
        delete leafletCut.validTo
      }
      const response = await updateLeaflet(leafletCut, storeId)

      if (response.status === 204) {
        commit('SET_LEAFLET_STATUS', {
          storeId,
          leafletId: leafletCut.id,
          status: statusList.READY_FOR_ACTIVATION.value
        })
      }

      if (
        shopData.filterByInactive &&
        !shopData.leaflets.find(leaflet => leaflet.status === statusList.INACTIVE.value).length
      ) {
        commit('SWITCH_STORE_INACTIVE_FILTER', storeId)
      }
    },

    async fetchLeafletsSummary({ commit }, storeId) {
      const leafletsSummary = await getLeafletsSummary(storeId)

      if (leafletsSummary.status === 200) {
        commit('SET_LEAFLETS_SUMMARY', { storeId, leafletsSummary: leafletsSummary.data })
      }
    }
  }
}

export default shops
